<template>
  <div>
    <v-tooltip :disabled="!(!currentRecording || noValidChunks || mini)" bottom>
      <template v-slot:activator="{ on }">
        <span v-on="on">
          <v-btn
            class="px-4"
            :disabled="!currentRecording || noValidChunks"
            color="#eeeeee"
            elevation="0"
            height="32"
            @click.exact="openOverlay"
          >
            <p v-if="!mini" class="mb-0">{{ $t("Delete") }}</p>
            <v-icon
              size="16"
              class="mb-1"
              :class="{ 'ml-2': !mini }"
              color="grey darken-2"
            >
              fa-trash-alt
            </v-icon>
          </v-btn>
        </span>
      </template>
      <span>{{
        !!currentRecording && noValidChunks
          ? $t(
              "This recording is being processed, you can delete this in 15 minutes"
            )
          : !!currentRecording
          ? $t("Delete Footage")
          : $t("Please select a valid recording")
      }}</span>
    </v-tooltip>
    <v-dialog no-click-animation width="600px" v-model="showDialog">
      <v-card v-if="showDialog">
        <v-card-text class="px-9 pt-5 pb-0">
          <v-list>
            <v-row justify="center">
              <v-col class="px-1">
                <p class="pb-12 px-0">
                  {{
                    $t(
                      "Choose a start and end time to delete from the recording"
                    )
                  }}:
                </p>
                <vue-slider
                  class="pb-10 px-5"
                  v-model="sliderValue"
                  :marks="marks"
                  :drag-on-click="false"
                  :enable-cross="false"
                  :adsorb="true"
                  :included="true"
                  :tooltip="'always'"
                  :min="min"
                  :max="max"
                  :tooltip-placement="['top', 'bottom']"
                  :process-style="{ backgroundColor: primaryColor }"
                  :clickable="false"
                >
                  <template v-slot:tooltip="{ value, focus }">
                    <div :class="['custom-tooltip', { focus }]">
                      {{ formatDate(addMiliseconds(value), true, showSeconds) }}
                    </div>
                  </template>
                  <template v-slot:label="{ label, active }">
                    <div
                      :class="[
                        'vue-slider-mark-label',
                        'custom-label',
                        { active }
                      ]"
                    >
                      {{ label }}
                    </div>
                  </template>
                </vue-slider>
                <p v-if="!recording.closed" class="mt-8 mb-0 px-1 pb-0">
                  {{
                    $t(
                      "The most recent 15 minutes are being processed and cannot be deleted yet. Please try again in 15 minutes."
                    )
                  }}
                </p>
              </v-col>
            </v-row>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="mr-3 mb-3"
            :disabled="sliderValue[0] === sliderValue[1]"
            depressed
            @click="openConfirmOverlay"
            >{{ $t("Delete") }}</v-btn
          >
          <v-btn class="mr-3 mb-3" depressed @click="closeOverlay">{{
            $t("Close")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmDialog" width="500">
      <v-card class="pa-3">
        <v-card-text>
          <p class="pt-6">{{ $t("Please enter your password to confirm.") }}</p>
          <v-text-field
            type="password"
            v-model="userPassword"
            class="pt-2"
            :placeholder="$t('Password')"
            :error-messages="userPasswordErrors"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            depressed
            color="primary"
            :disabled="!passwordDirty || !!userPasswordErrors.length"
            @click="deleteChunks"
          >
            {{ $t("Confirm") }}
          </v-btn>
          <v-btn depressed @click.stop="confirmDialog = false">
            {{ $t("Close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  reactive,
  toRefs,
  computed,
  watch,
} from "../../plugins/compositionApi";
import { t } from "../../service/locale";
import { store } from "@/store";
import restapi from "../../service/CMApi";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";
import colors from "vuetify/lib/util/colors";
import { getMessageFromError } from "../../service/errors";
import Sentry from '../../plugins/sentry';

export default {
  name: "DeleteRecordings",
  components: {
    VueSlider
  },
  props: {
    camera: {
      type: Object,
    },
    currentRecording: Object,
    currentTime: Number,
    timeLevel: Number,
    eventFilter: Array,
    chunks: Array,
    currentChunk: Object,
    mini: Boolean,
    offset: Number,
    rights: Object
  },
  setup(props, context) {
    const { formatDate, addMiliseconds, removeMiliseconds } = timeHelpers(props);
    const data = reactive({
      noValidChunks: computed(
        () => data.filteredChunks.length === 1 && !data.filteredChunks[0].closed
      ),
      confirmDialog: false,
      userPassword: "",
      passwordDirty: false,
      userPasswordErrors: computed(() => {
        const errors = [];
        if (!data.passwordDirty) return errors;
        if (data.userPassword === "") errors.push("Password is required");
        return errors;
      }),
      cameraId: computed(() => props.camera.cameraId),
      recordingId: computed(() =>
        props.currentRecording ? props.currentRecording.recordingId : null
      ),
      recording: computed(() => props.currentRecording),
      showDialog: false,
      primaryColor: colors.primary,
      sliderValue: [0, 50],
      startTime: null,
      endTime: null,
      firstDeletedChunk: null,
      lastDeletedChunk: null,
      min: computed(() =>
        data.startTime
          ? data.startTime
          : removeMiliseconds(new Date().getTime() - 3600000)
      ),
      max: computed(() =>
        data.endTime ? data.endTime : removeMiliseconds(new Date().getTime())
      ),
      showSeconds: true,
      filteredChunks: computed(() => {
        return props.chunks.filter(e => {
          let currentTime = removeMiliseconds(
            new Date(props.currentTime).getTime()
          );
          let st = removeMiliseconds(new Date(e.startTime).getTime());
          return st > currentTime - 10800 && st < currentTime + 10800;
        });
      }),
      marks: computed(() => {
        let marks = {};

        if (!data.noValidChunks) {
          if (data.filteredChunks.length) {
            let dt = new Date(data.filteredChunks[0].startTime).getTime();
            data.startTime = removeMiliseconds(dt);
            marks[removeMiliseconds(dt)] = {
              label: ""
            };
          }
          let lastPoint = 0;
          data.filteredChunks.forEach(chunk => {
            if (chunk.closed) {
              let et = new Date(chunk.endTime).getTime();
              marks[removeMiliseconds(et)] = {
                label: ""
              };
              lastPoint = et > lastPoint ? et : lastPoint;
            }
          });

          data.endTime = removeMiliseconds(lastPoint);
        }

        return marks;
      })
    });

    watch(
      () => data.showDialog,
      (newValue, oldValue) => {
        if (newValue) {
          data.userPassword = "";
          data.passwordDirty = false;

          if (props.currentChunk.closed) {
            data.sliderValue = [
              removeMiliseconds(
                new Date(props.currentChunk.startTime).getTime()
              ),
              removeMiliseconds(new Date(props.currentChunk.endTime).getTime())
            ];
          } else {
            data.sliderValue = [
              removeMiliseconds(
                new Date(data.filteredChunks[0].startTime).getTime()
              ),
              removeMiliseconds(
                new Date(data.filteredChunks[0].endTime).getTime()
              )
            ];
          }
        }
      }
    );

    watch(
      () => data.userPassword,
      (newValue, oldValue) => {
        if (newValue !== "") {
          data.passwordDirty = true;
        }
      }
    );

    const { openOverlay, closeOverlay, openConfirmOverlay } = useOverlay(data, context, props);
    const { deleteChunks } = useChunks(data, context, props);

    return {
      ...toRefs(data),
      openOverlay,
      closeOverlay,
      formatDate,
      addMiliseconds,
      deleteChunks,
      openConfirmOverlay
    };
  }
};

function useChunks(data, context, props) {
  const { formatDate, addMiliseconds, removeMiliseconds } = timeHelpers(props);
  const { openOverlay, closeOverlay } = useOverlay(data, context);

  async function deleteChunks() {
    try {
      let validPassword = await restapi.isPasswordValid(data.userPassword);
    } catch (error) {
      store.dispatch("toastMessage", {
        showing: true,
        text: t(`Wrong credentials`),
        timeout: -1,
        color: "error",
        support: true
      });
      return;
    }
    try {
      data.firstDeletedChunk = data.filteredChunks.find(
        chunk =>
          Math.abs(
            removeMiliseconds(new Date(chunk.startTime).getTime()) -
              data.sliderValue[0]
          ) < 10
      );
      data.lastDeletedChunk = data.filteredChunks.find(
        chunk =>
          Math.abs(
            removeMiliseconds(new Date(chunk.endTime).getTime()) -
              data.sliderValue[1]
          ) < 10
      );
      
      let firstChunkId = data.firstDeletedChunk.recordingPartId;
      let lastChunkId = data.lastDeletedChunk.recordingPartId;
      
      await restapi.deleteChunks(
        data.cameraId,
        data.recordingId,
        firstChunkId,
        lastChunkId
      );
      closeOverlay(true);
    } catch (error) {
      Sentry.captureException(error)
      store.dispatch("toastMessage", {
        showing: true,
        text: getMessageFromError(error, "DELETE_RECORDING"),
        timeout: -1,
        color: "error",
        support: true
      });
    }
  }

  return { deleteChunks };
}

function timeHelpers(props) {
  function formatDate(time, hasTime = true, showSeconds = false) {
    let date = new Date(time - props.offset);
    let day = ("0" + date.getDate()).slice(-2);
    let month = ("0" + (date.getMonth() + 1)).slice(-2);
    if (hasTime) {
      let hour = ("0" + date.getHours()).slice(-2);
      let minute = ("0" + date.getMinutes()).slice(-2);
      let seconds = ("0" + date.getSeconds()).slice(-2);
      if (showSeconds) {
        return `${day}/${month} ${hour}:${minute}:${seconds}`;
      } else {
        return `${day}/${month} ${hour}:${minute}`;
      }
    } else {
      return `${day}/${month}`;
    }
  }

  function removeMiliseconds(time) {
    return parseInt(time.toString().slice(0, -3));
  }

  function addMiliseconds(time) {
    return parseInt(time.toString() + "000");
  }

  return { formatDate, addMiliseconds, removeMiliseconds };
}

function useOverlay(data, context, props) {
  function openOverlay() {
    data.showDialog = true;
    context.emit("pause");
  }

  function openConfirmOverlay() {
    if (props.rights && props.rights.demoUser) {
      return store.dispatch("demoMessage")
    }
    data.confirmDialog = true;
    data.showDialog = false;
  }

  function closeOverlay(updateRecordings = false) {
    data.showDialog = false;
    data.confirmDialog = false;

    if (updateRecordings) {
      context.emit("updateRecordings", {
        firstDeletedChunk: data.firstDeletedChunk,
        lastDeletedChunk: data.lastDeletedChunk
      });
    }
  }

  return { openOverlay, closeOverlay, openConfirmOverlay };
}
</script>

<style lang="scss" scoped>
@import "../../assets/styles/main";

.custom-tooltip {
  background-color: grey;
  color: white;
  border-radius: 3px;
  padding: 1px 4px 0 4px;
  text-align: center;
  // white-space: pre;
}
.custom-label {
  width: 60px;
  white-space: pre-wrap;
  text-align: center;
}
.custom-label.active {
  color: #3498db;
}
.black-label-text {
  &::v-deep label {
    color: #555555;
    font-size: 14px;
  }
}
.overlay-z {
  z-index: 1000 !important;
}
</style>
